import { Injectable } from '@angular/core';
import {
    Action, Action as ActionSelector, Selector, State, StateContext
} from '@ngxs/store';
import {
    append, iif, insertItem, patch, removeItem, updateItem
} from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Mapping } from '../../services/dtos/mapping';
import { MappingService } from '../../services/mapping.service';
import { AddCustomerMapping } from '../customers-state/customers.actions';
import {
  AddMapping,
  AddRule,
  ClearCurrentMappingId,
  ClearCurrentRuleId as ClearActivePluginId,
  ClearCurrentRuleId,
  DeleteMapping,
  DeleteRule,
  EditRule,
  FetchAllMappings,
  FetchMapping,
  SetActivePluginId,
  SetCurrentMappingId,
  SetCurrentRuleId,
  SetCustomerMappingIds,
  UpdateMapping,
  UpdateMappingValues
} from './mapping.actions';

export class MappingStateModel {
  public mappings!: Mapping[];
  public selectedMappingId!: string;
  public selectedRuleId!: string;
  public activePluginId!: string;
  public customerMappingIds!: string[];
}

const defaults = {
  mappings: [],
  selectedMappingId: '',
  selectedRuleId: '',
  activePluginId: '',
  customerMappingIds: [],
};

@State<MappingStateModel>({
  name: 'mappingState',
  defaults,
})
@Injectable()
export class MappingState {
  public constructor(private mappingService: MappingService) {}

  @Selector()
  public static getAll(state: MappingStateModel): Mapping[] {
    return state.mappings as Mapping[];
  }

  @Selector()
  public static getSelectedMappingId(state: MappingStateModel): string {
    return state.selectedMappingId;
  }

  @Selector()
  public static getSelectedMapping(
    state: MappingStateModel
  ): Mapping | undefined {
    return state.mappings.find(({ id }) => id === state.selectedMappingId);
  }

  @Selector()
  public static getSelectedRuleId(state: MappingStateModel): string {
    return state.selectedRuleId;
  }

  @Selector()
  public static getActivePluginId(state: MappingStateModel): string {
    return state.activePluginId;
  }

  @Selector()
  public static getCustomerMappingIds(state: MappingStateModel): string[] {
    return state.customerMappingIds;
  }

  @ActionSelector(FetchAllMappings)
  public fetchAll(
  {
    getState,
    setState,
  }: StateContext<MappingStateModel>, 
  { customerId }: FetchAllMappings): Observable<Mapping[]> {
    return this.mappingService.getAll(customerId).pipe(
      tap((mappings: Mapping[]) => {
        const state = getState();
        setState({
          ...state,
          mappings,
        });
      })
    );
  }

  @ActionSelector(FetchMapping)
  public fetchSingle(
    { setState }: StateContext<MappingStateModel>,
    { id, withValues }: FetchMapping
  ): Observable<Mapping> {
    return this.mappingService.getMapping(id, withValues).pipe(
      tap((mapping: Mapping) => {
        setState(
          patch<MappingStateModel>({
            mappings: iif<Mapping[]>(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              (mappings) => mappings!.some((x) => x.id === mapping.id),
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              updateItem<Mapping>((x: Mapping) => x!.id === mapping.id, mapping),
              insertItem<Mapping>(mapping)
            ),
          })
        );
      })
    );
  }

  @Action(SetCurrentMappingId)
  public setCurrentMappingId(
    { setState }: StateContext<MappingStateModel>,
    { id }: SetCurrentMappingId
  ): void {
    setState(
      patch({
        selectedMappingId: id,
      })
    );
  }

  @Action(ClearCurrentMappingId)
  public clearCurrentMappingId({
    setState,
  }: StateContext<MappingStateModel>): void {
    setState(
      patch({
        selectedMappingId: '',
      })
    );
  }

  @Action(AddMapping)
  public add(
    { setState, dispatch }: StateContext<MappingStateModel>,
    { payload }: AddMapping
  ): Observable<string> {
    return this.mappingService.addMapping(payload).pipe(
      tap((mappingId: string) => {
        payload.id = mappingId;
        dispatch(new AddCustomerMapping(mappingId));
        setState(
          patch({
            mappings: append<Mapping>([payload]),
            selectedMappingId: mappingId
          })
        );
      })
    );
  }

  @Action(UpdateMapping)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public update(
    { setState, getState }: StateContext<MappingStateModel>,
    { payload }: UpdateMapping
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.mappingService.updateMapping(payload).pipe(
      tap(() => {
        const state = getState();
        const mapping = state.mappings.find((x) => x.id === payload.id);
        const updated = {
          ...mapping,
          name: payload.name,
          columns: payload.columns,
        } as Mapping;
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
            mappings: updateItem<Mapping>((x: Mapping) => x!.id === payload.id, updated),
          })
        );
      })
    );
  }

  @Action(DeleteMapping)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public deleteMapping(
    { setState }: StateContext<MappingStateModel>,
    { mappingId }: DeleteMapping
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.mappingService.delete(mappingId).pipe(
      tap(() => {
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
            mappings: removeItem<Mapping>((x) => x!.id === mappingId),
          })
        );
      })
    );
  }

  @Action(UpdateMappingValues)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateMappingValues(
    { setState, getState }: StateContext<MappingStateModel>,
    { payload }: UpdateMappingValues
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.mappingService.updateMappingValues(payload).pipe(
      tap(() => {
        const state = getState();
        const mapping = state.mappings.find((x) => x.id === payload.id);
        const updated = {
          ...mapping,
          values: JSON.parse(payload.jValues),
        } as Mapping;
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
            mappings: updateItem<Mapping>((x: Mapping) => x!.id === payload.id, updated),
          })
        );
      })
    );
  }

  @Action(AddRule)
  public addRule(
    { dispatch }: StateContext<MappingStateModel>,
    { payload, customerId }: AddRule
  ): Observable<string> {
    return this.mappingService.addRule(payload).pipe(
      tap(() => {
        dispatch(new FetchAllMappings(customerId));
      })
    );
  }

  @Action(EditRule)
  public editRule(
    { dispatch }: StateContext<MappingStateModel>,
    { payload, customerId }: EditRule
  ): Observable<string> {
    return this.mappingService.updateRule(payload).pipe(
      tap(() => {
        dispatch(new FetchAllMappings(customerId));
      })
    );
  }

  @Action(SetCurrentRuleId)
  public setCurrentRuleId(
    { setState }: StateContext<MappingStateModel>,
    { id }: SetCurrentRuleId
  ): void {
    setState(
      patch({
        selectedRuleId: id,
      })
    );
  }

  @Action(ClearCurrentRuleId)
  public clearCurrentRuleId({
    setState,
  }: StateContext<MappingStateModel>): void {
    setState(
      patch({
        selectedRuleId: '',
      })
    );
  }

  @Action(DeleteRule)
  public deleteRule(
    { dispatch }: StateContext<MappingStateModel>,
    { ruleId, customerId }: DeleteRule
  ): Observable<string> {
    return this.mappingService.deleteRule(ruleId).pipe(
      tap(() => {
        dispatch(new FetchAllMappings(customerId));
      })
    );
  }

  @Action(SetActivePluginId)
  public setActivePluginId(
    { setState }: StateContext<MappingStateModel>,
    { id }: SetCurrentRuleId
  ): void {
    setState(
      patch({
        activePluginId: id,
      })
    );
  }

  @Action(ClearActivePluginId)
  public clearActivePluginId({
    setState,
  }: StateContext<MappingStateModel>): void {
    setState(
      patch({
        activePluginId: '',
      })
    );
  }

  @Action(SetCustomerMappingIds)
  public setCustomerMappingIds(
    { setState }: StateContext<MappingStateModel>,
    { mappingIds }: SetCustomerMappingIds
  ): void {
    setState(
      patch({
        customerMappingIds: mappingIds,
      })
    );
  }
}
